<!-- DocuSign signing-in-progress overlay (after Sign and Submit) -->
@if (isDocuSignSigningInProgress) {
<div
  class="fixed inset-0 z-50 flex items-center justify-center bg-white/30"
  style="backdrop-filter: blur(1px);"
>
  <app-loader
    type="fullscreen"
    message="Preparing DocuSign signing..."
    size="large"
    ariaLabel="Preparing DocuSign signing"
  >
  </app-loader>
</div>
}

<div class="flex-1 flex flex-col overflow-auto overflow-x-hidden">
  <!-- Routing Slips Content -->
  <main class="flex-1 overflow-y-auto overflow-x-hidden p-6 ipad-scrollable relative">
    <!-- Full Page Loader -->
    @if (pageLoading) {
    <div
      class="flex items-center justify-center bg-white bg-opacity-95 absolute inset-0 z-10"
      style="min-height: calc(100vh - 80px); backdrop-filter: blur(2px);"
    >
      <app-loader
        type="fullscreen"
        message="Loading routing slips..."
        size="large"
        ariaLabel="Loading routing slips page"
      >
      </app-loader>
    </div>
    }
    <!-- Content Area - Only show when not loading -->
    @if (!pageLoading) {
    <!-- Top Section: Routing Slip Title, Filter Tabs, Search, and Sort in one line -->
    <div
      class="flex flex-col md:flex-row md:items-center md:justify-between lg:flex-row lg:items-center lg:justify-between mb-6 gap-3"
    >
      <!-- Left: Routing Slip Title -->
      <h1 class="hidden md:block text-xl font-bold text-black-600">
        Routing Slip
      </h1>
      <!-- MOBILE: Row 1 (Filter + Sort + Add next to title) -->
      <div class="flex md:hidden flex-row items-center justify-between w-full">
        <!-- Left side -->
        <h1 class="text-xl font-bold text-black-600">Routing Slip</h1>

        <!-- Right side -->
        <div class="flex flex-row items-center gap-2 header-controls">
          <app-filter-tabs
            [tabs]="filterOptions"
            [activeTab]="selectedFilter"
            (tabChange)="onFilterTabChange($event)"
          ></app-filter-tabs>
          <app-sort-dropdown
            [options]="sortOptions"
            [selectedOption]="selectedSortOption"
            [placeholder]="'Sort By:'"
            (sortChange)="onSortChange($event)"
          ></app-sort-dropdown>
          @if (!isManager) {
          <button
            pButton
            type="button"
            label="+"
            class="add-new-btn"
            routerLink="/routing-slip/create"
          ></button>
          }
        </div>
      </div>

      <!-- DESKTOP/TABLET: Right side inline (Filter + Search + Sort + Add) -->
      <div
        class="hidden md:flex flex-wrap lg:flex-nowrap items-center gap-2 header-controls"
      >
        <app-filter-tabs
          [tabs]="filterOptions"
          [activeTab]="selectedFilter"
          (tabChange)="onFilterTabChange($event)"
        ></app-filter-tabs>

        <div class="search-bar-wrapper">
          <app-search-bar
            [placeholder]="'Search'"
            [value]="searchTerm"
            (searchChange)="onSearchChange($event)"
          ></app-search-bar>
          <button
            *ngIf="searchTerm && searchTerm.trim().length > 0"
            type="button"
            class="search-clear-button"
            (click)="clearSearch()"
            aria-label="Clear search"
            title="Clear search"
          >
            <i class="pi pi-times"></i>
          </button>
        </div>

        <app-sort-dropdown
          [options]="sortOptions"
          [selectedOption]="selectedSortOption"
          [placeholder]="'Sort By:'"
          (sortChange)="onSortChange($event)"
        ></app-sort-dropdown>

        @if (!isManager) {
        <button
          pButton
          type="button"
          label="+Add New"
          class="add-new-btn"
          routerLink="/routing-slip/create"
        ></button>
        }
      </div>

      <!-- MOBILE: Row 2 (Search only - full width) -->
      <div class="flex md:hidden items-center w-full mt-2">
        <div class="search-bar-wrapper w-full">
          <app-search-bar
            class="w-full"
            [placeholder]="'Search'"
            [value]="searchTerm"
            (searchChange)="onSearchChange($event)"
          ></app-search-bar>
          <button
            *ngIf="searchTerm && searchTerm.trim().length > 0"
            type="button"
            class="search-clear-button"
            (click)="clearSearch()"
            aria-label="Clear search"
            title="Clear search"
          >
            <i class="pi pi-times"></i>
          </button>
        </div>
      </div>
    </div>

    @if (loading && !pageLoading) {
    <!-- Loading State -->
    <div class="flex items-center justify-center min-h-[400px]">
      <app-loader
        type="inline"
        message="Loading routing slips..."
        size="large"
        ariaLabel="Loading routing slips"
      >
      </app-loader>
    </div>
    } @else if (!pageLoading) {
    <!-- DESKTOP (XL and larger) -->
    <div
      class="hidden xl:block bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
    >
      <app-table
        [data]="routingSlips"
        [columns]="columns"
        [loading]="loading"
        [context]="'routing-slip'"
        [isArchivedView]="selectedFilter === 'archive'"
        [getPermissionForRow]="getPermissionForRoutingSlipRow.bind(this)"
        (rowClick)="onRowClick($event)"
        (comment)="onComment($event)"
        (auditLog)="onAuditLog($event)"
        (approve)="onApprove($event)"
        (reject)="onReject($event)"
        (edit)="onEdit($event)"
        (delete)="onArchive($event)"
        (documentNameClick)="onDocumentNameClick($event)"
      ></app-table>
      <app-pagination
        [paginationInfo]="paginationInfo"
        (pageChange)="onPageChange($event)"
      ></app-pagination>
    </div>

    <!-- MOBILE/iPad Pro -->
    <div class="xl:hidden w-full max-w-full overflow-x-hidden">
      <app-card-list
        [data]="routingSlips"
        [context]="'routing-slips'"
        [isArchivedView]="selectedFilter === 'archive'"
        [getPermissionForRow]="getPermissionForRoutingSlipRow.bind(this)"
        (rowClick)="onRowClick($event)"
        (auditLog)="onAuditLog($event)"
        (approve)="onApprove($event)"
        (reject)="onReject($event)"
        (edit)="onEdit($event)"
        (delete)="onArchive($event)"
        (documentNameClick)="onDocumentNameClick($event)"
      ></app-card-list>

      <!-- Pagination for Mobile/iPad Pro -->
      <app-pagination
        [paginationInfo]="paginationInfo"
        (pageChange)="onPageChange($event)"
      ></app-pagination>
    </div>
    }
    }
  </main>

  <!-- Comment Modal -->
  <app-modal
    [visible]="showModal"
    [modalType]="modalType"
    [title]="modalTitle"
    [width]="'500px'"
    (visibleChange)="showModal = $event"
    (close)="onModalClose()"
    (submit)="onModalSubmit($event)"
  ></app-modal>

  <app-audit-modal
    [(visible)]="auditDialogVisible"
    [logs]="auditData"
    [row]="selectedRow"
    [loading]="auditLoading"
    (close)="onAuditClose()"
  ></app-audit-modal>

  <!-- Document Viewer Modal -->
  <app-document-viewer-modal
    [(visible)]="showDocumentViewer"
    [attachment]="currentDocument"
    [loading]="documentLoading"
    [documentName]="currentDocumentName"
    (visibleChange)="showDocumentViewer = $event"
    (download)="onDocumentDownload($event)"
    (openInNewTab)="onDocumentOpenInNewTab($event)"
    (close)="onDocumentViewerClose()"
  ></app-document-viewer-modal>

  <!-- Delete/Archive Confirmation Modal -->
  <app-modal
    [visible]="showDeleteModal"
    [modalType]="'confirm'"
    [title]="'Archive Routing Slip'"
    [message]="'Are you sure you want to archive this routing slip? This action can be undone from the Archived tab.'"
    [confirmButtonText]="'Archive'"
    [cancelButtonText]="'Cancel'"
    [confirmButtonDisabled]="deleteLoading"
    [width]="'450px'"
    (visibleChange)="showDeleteModal = $event"
    (close)="cancelDelete()"
    (confirm)="confirmDelete()"
  ></app-modal>

  <!-- Toast Messages -->
  <p-toast position="top-right"></p-toast>
</div>
